import {MiniSignal} from "p3";
import Entity from "../Entity";
import definitions from "../../definitions";
import BossFightScene from "../../scenes/BossFightScene";
import { weightedRand } from "../../utils";

export default class Boss extends Entity {
    /**
     * @param {string} id
     */
    constructor(id) {
        super();

        this._id = id;
        this._state = null;
        this._isDead = false;
        this._iconTextureId = "";
        this._name = "";

        // DEFAULT STATS FOR A BOSS
        this._missChance = 0.3; // probability of missing
        this._hitChance = 0.8; // probability of hitting
        this._minDamage = 0;
        this._maxDamage = 8; 
        this._health = 10;
        //

        this.signals.hit = new MiniSignal();
        this.signals.dead = new MiniSignal();
        this.signals.bossStrike = new MiniSignal();

        this._damageEffectFilter = null;
        this._damageEffectTween = null;
    }

    init() {
        super.init();

        this._damageEffectFilter = new P3.filters.AdditiveFilter(0, 3);
    }

    destroy() {
        super.destroy();
    }

    update() {
        super.update();

        this.isStateValid(this._state) && this.onStateUpdate(this._state);
    }

    takeDamage(amount) {
        if (this.isDead) return false;
    
        this.damageEffect().then();

        this._health -= amount;
        this.onTakeDamage(amount);

        this.isDead && this.onDead();

        this.state = this.isDead ? definitions.BOSS_STATE.DEAD : definitions.BOSS_STATE.TAKE_DAMAGE;
        
        return this.isDead;
    }

    removeDamageEffect() {
        if (this._damageEffectTween) {
            BossFightScene.active.removeTween(this._damageEffectTween, true);
            this._damageEffectTween = null;
        }

        this._damageEffectFilter.blendStrength = 0;
        this.filters = [];
    }

    /**
     * @return {Promise}
     */
    damageEffect() {
        return new Promise((resolve) => {
            this.removeDamageEffect();

            this._damageEffectFilter.blendStrength = 0.3;
            this._damageEffectTween = BossFightScene.active.addTween(gsap.to(this._damageEffectFilter, 0.24, {
                blendStrength: 0,
                ease: Power2.easeOut,
                onStart: () => {
                    this.filters = [this._damageEffectFilter]
                },
                onComplete: () => {
                    this.filters = [];
                    resolve();
                }
            }));
        });
    }

    getDamageAmount() {
        let weights = { 
            0: this.missChance, 
            1: this.hitChance 
        };
        let action = weightedRand(weights);

        if(action == 1) {
            this.signals.hit.dispatch();
            return Math.floor(Math.random() * (this.maxDamage - this.minDamage)) + this.minDamage;
        } 
        else {
            return 0;
        }
    }
    
    onViewAdded() {
        super.onViewAdded();
        this.state = definitions.ENEMY_STATE.IDLE;
    }

    /**
     * @param {number} state
     * @return {boolean}
     */
    isStateValid(state) {
        return typeof state === "number" && state >= 0;
    }

    /**
     * @param {number} damage
     */
    onTakeDamage(damage) {}

    onDead() {
        this.signals.dead.dispatch(this);
    }

    /**
     * @param {number} state
     */
    onStateEnter(state) {
        ////console.log("enter:", state);
    }

    /**
     * @param {number} state
     */
    onStateUpdate(state) {
        ////console.log("update:", state);
    }

    /**
     * @param {number} state
     */
    onStateExit(state) {
        ////console.log("exit:", state);
    }

    get id() {
        return this._id;
    }

    /**
     * @return {number}
     */
    get state() {
        return this._state;
    }
    
    /**
     * @param {number} value
     */
    set state(value) {
        const previous = this._state;
        this._state = value;
    
        this.isStateValid(previous) && this.onStateExit(previous);
        this.isStateValid(this._state) && this.onStateEnter(this._state);
    }

    get missChance() {
        return this._missChance;
    }

    set missChance(value) {
        this._missChance = value;
    }

    get hitChance() {
        return this._hitChance;
    }

    set hitChance(value) {
        this._hitChance = value;
    }

    get isDead() {
        return this._health <= 0;
    }

    get health() {
        return this._health;
    }

    set health(value) {
        this._health = Math.max(0, value);
    }

    get iconTextureId() {
        return this._iconTextureId;
    }

    set iconTextureId(value) {
        this._iconTextureId = value;
    }

    get name() {
        return this._name;
    }

    set name(value) {
        this._name = value;
    }

    get minDamage() {
        return this._minDamage;
    }

    set minDamage(value) {
        this._minDamage = value;
    }

    get maxDamage() {
        return this._maxDamage;
    }

    set maxDamage(value) {
        this._maxDamage = value;
    }
}